import * as THREE from 'three'
import type { MouthShape } from '../audio/CutsceneLipSync'
import type { Beat, Character } from '../data/cutscenes'
import type { AdaptiveQualityTier } from '../engine/AdaptiveQuality'
import { UnderwaterAtmosphere } from './UnderwaterAtmosphere'
import { DioramaActors } from './DioramaActors'
import { clearPlacementXZ, DioramaExtras } from './DioramaExtras'
import { dioramaArt } from './DioramaArt'
import { lockDioramaSkybox } from './DioramaSkybox'
import { DioramaStoryProps, type StoryPropSnapshot } from './DioramaStoryProps'
import { DioramaSet, dioramaDetailStatsForTier, type DioramaDetailStats } from './DioramaSets'
import { smoothFocusDistance } from '../engine/cutscene-optics'
import type { CollisionBody } from './CollisionGuard'
import { boundsOf, separateXZ } from './CollisionGuard'

export interface DioramaAssetState {
  ready: boolean
  failed: string[]
}

/** Energy-balanced for the renderer's PMREM environment and ACES exposure. */
export const DIORAMA_LIGHTING_PROFILE = {
  environment: 0.19,
  hemi: 0.34,
  key: 1.44,
  fill: 0.28,
  rim: 0.7,
  accent: 0.24,
} as const

/** Isolated Three.js story stage rendered through the game's existing Renderer. */
export class DioramaStage {
  readonly scene = new THREE.Scene()
  readonly camera = new THREE.PerspectiveCamera(42, 16 / 9, 0.1, 120)
  readonly actors = new DioramaActors()
  private readonly extras = new DioramaExtras()
  private readonly storyProps = new DioramaStoryProps()
  readonly assets: DioramaAssetState = { ready: false, failed: [] }
  readonly ready: Promise<DioramaAssetState>

  private readonly atmosphere = new UnderwaterAtmosphere()
  private set: DioramaSet | null = null
  private beat: Beat | null = null
  private skyboxScene: Beat['scene'] | null = null
  private qualityTier: AdaptiveQualityTier = 4
  private reducedMotion = false
  private disposed = false
  private readyPending = true
  private cameraBase = new THREE.Vector3(0, 3.6, 10.5)
  private lookBase = new THREE.Vector3(0, 1.35, 0)
  private cameraTarget = new THREE.Vector3()
  private lookTarget = new THREE.Vector3()
  private lookCurrent = new THREE.Vector3()
  private cameraAnchor = new THREE.Vector3()
  private anchorPending = true
  private ambientElapsed = 0
  private focusDistance = 15
  private targetFocusDistance = 15
  private lastCollisionCorrections: string[] = []
  private readonly fog = new THREE.FogExp2(0x07344a, 0.025)
  private hemi = new THREE.HemisphereLight(0x9fe9ff, 0x06283a, DIORAMA_LIGHTING_PROFILE.hemi)
  private key = new THREE.DirectionalLight(0xfff4d6, DIORAMA_LIGHTING_PROFILE.key)
  private fill = new THREE.DirectionalLight(0x77d9ff, DIORAMA_LIGHTING_PROFILE.fill)
  private rim = new THREE.DirectionalLight(0xff69b4, DIORAMA_LIGHTING_PROFILE.rim)

  constructor(onAssetsChanged?: (state: DioramaAssetState) => void) {
    this.scene.name = 'CutsceneDioramaScene'
    this.scene.background = new THREE.Color(0x07344a)
    this.scene.environmentIntensity = DIORAMA_LIGHTING_PROFILE.environment
    this.scene.fog = this.fog
    this.camera.position.copy(this.cameraBase)
    this.camera.lookAt(this.lookBase)
    this.key.position.set(-7, 12, 6)
    this.key.castShadow = true
    this.key.shadow.mapSize.set(2048, 2048)
    Object.assign(this.key.shadow.camera, { left: -13, right: 13, top: 12, bottom: -12, near: 0.5, far: 42 })
    this.key.shadow.bias = -0.0003
    this.key.shadow.normalBias = 0.035
    this.key.shadow.radius = 3
    this.fill.position.set(5, 3, 8)
    this.rim.position.set(6, 5, -5)
    this.scene.add(this.atmosphere.root, this.hemi, this.key, this.fill, this.rim, this.actors.group, this.extras.root, this.storyProps.root)

    this.ready = Promise.all([this.actors.ready, this.storyProps.ready]).then(([failed]) => {
      if (!this.readyPending) return { ready: false, failed: [] }
      this.readyPending = false
      this.assets.ready = true
      this.assets.failed = failed
      onAssetsChanged?.(this.assets)
      return this.assets
    })
  }

  setBeat(beat: Beat): void {
    if (this.disposed) return
    const skybox = lockDioramaSkybox(this.skyboxScene, beat.scene)
    this.skyboxScene = skybox.scene
    this.beat = beat
    if (this.set?.sceneId === beat.scene) this.set.setBeat(beat)
    else {
      this.set?.dispose()
      this.set = new DioramaSet(beat.scene, beat)
      this.set.setDetailTier(this.qualityTier)
      this.scene.add(this.set.root)
    }
    this.actors.setCast(beat.cast, beat.scene)
    this.extras.setBeat(beat)
    this.storyProps.root.visible = true
    this.storyProps.setBeat(beat)

    const art = dioramaArt(beat.scene)
    ;(this.scene.background as THREE.Color).setHex(skybox.color)
    // Mutate one fog instance and use the first shot's atmosphere for the whole sequence. Replacing
    // the fog object per beat made distant geometry and the horizon re-light in the same frame as a
    // camera cut, which read as a skybox color pop on slower displays.
    this.fog.color.setHex(skybox.color)
    this.fog.density = skybox.fogDensity
    this.atmosphere.setColor(skybox.color, skybox.fogDensity)
    this.atmosphere.setReducedMotion(this.reducedMotion)
    this.hemi.color.setHex(art.fill)
    this.hemi.groundColor.setHex(0x273b40)
    this.key.color.setHex(art.key)
    this.key.intensity = DIORAMA_LIGHTING_PROFILE.key
    this.hemi.intensity = DIORAMA_LIGHTING_PROFILE.hemi
    this.fill.intensity = DIORAMA_LIGHTING_PROFILE.fill
    this.rim.intensity = DIORAMA_LIGHTING_PROFILE.rim
    this.fill.color.setHex(art.fill)
    this.rim.color.setHex(art.rim)
    this.anchorPending = true
    this.composeCamera(beat)
    this.update(0, 0, 0, false, null)
  }

  get detailStats(): DioramaDetailStats {
    return this.set?.detailStats ?? dioramaDetailStatsForTier(this.qualityTier)
  }

  /** Dev/E2E proof surface for authored motion and ground contacts. */
  animationSnapshot(): ReturnType<DioramaActors['animationSnapshot']> & { storyProps: StoryPropSnapshot[] } {
    return { ...this.actors.animationSnapshot(), storyProps: this.storyProps.storyPropSnapshot() } as ReturnType<DioramaActors['animationSnapshot']> & { storyProps: StoryPropSnapshot[] }
  }

  /** Every moving story object sampled in world space. This is deliberately
   * separate from animationSnapshot so a frame audit can check true mesh bounds,
   * rather than only actor root transforms.
   */
  collisionSnapshot(): Array<{
    name: string
    kind: CollisionBody['kind']
    purpose: string
    position: [number, number, number]
    bounds: ReturnType<typeof boundsOf>
  }> {
    return this.dynamicBodies().map((body) => ({
      name: body.name,
      kind: body.kind,
      purpose: body.purpose,
      position: [body.object.position.x, body.object.position.y, body.object.position.z],
      bounds: boundsOf(body.object),
    }))
  }

  collisionCorrections(): string[] { return [...this.lastCollisionCorrections] }

  /** Unresolved dynamic intersections after the solver, excluding contacts that
   * are explicitly called for by the current screenplay beat. This is the
   * frame-level acceptance surface used by the headless audit. */
  collisionIssues(): Array<{ a: string; b: string; depth: number; aPosition: [number, number, number]; bPosition: [number, number, number] }> {
    const bodies = this.dynamicBodies()
    const issues: Array<{ a: string; b: string; depth: number; aPosition: [number, number, number]; bPosition: [number, number, number] }> = []
    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const a = bodies[i]!, b = bodies[j]!
        const allowed = Boolean(a.object.userData.collisionContactAllowed || b.object.userData.collisionContactAllowed)
          || (a.name.startsWith('Extra_') && b.name.startsWith('Extra_'))
          || (a.kind === 'actor' && b.kind === 'actor'
            ? this.allowsActorContact(a.name, b.name)
            : a.kind === 'actor'
              ? this.allowsContact(b.name, a.name)
              : b.kind === 'actor'
                ? this.allowsContact(a.name, b.name)
                : this.allowsPropContact(a.name, b.name))
        if (allowed) continue
        const aa = boundsOf(a.object), bb = boundsOf(b.object)
        if (aa.minY >= bb.maxY || aa.maxY <= bb.minY) continue
        const x = Math.min(aa.maxX - bb.minX, bb.maxX - aa.minX)
        const z = Math.min(aa.maxZ - bb.minZ, bb.maxZ - aa.minZ)
        const depth = Math.min(x, z)
        if (depth > 0.12) issues.push({
          a: a.name,
          b: b.name,
          depth: Number(depth.toFixed(3)),
          aPosition: [a.object.position.x, a.object.position.y, a.object.position.z],
          bPosition: [b.object.position.x, b.object.position.y, b.object.position.z],
        })
      }
    }
    return issues
  }

  setReducedMotion(reduced: boolean): void {
    if (this.reducedMotion === reduced) return
    this.reducedMotion = reduced
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.readyPending = false
    this.actors.dispose()
    this.extras.dispose()
    this.storyProps.dispose()
    this.set?.dispose()
    this.set = null
    this.beat = null
    this.atmosphere.dispose()
    for (const light of [this.key, this.fill, this.rim]) light.shadow.dispose()
    this.scene.clear()
    this.scene.environment = null
    this.scene.environmentIntensity = 0
    this.scene.background = null
    this.scene.fog = null
  }

  setQualityTier(tier: AdaptiveQualityTier): void {
    this.qualityTier = tier
    this.set?.setDetailTier(tier)
  }

  update(dt: number, progress: number, elapsed: number, speaking: boolean, mouthCue: MouthShape | null): void {
    if (!this.beat || !this.set) return
    const ambient = this.reducedMotion ? 0 : this.ambientElapsed
    this.actors.update(this.beat, progress, ambient, speaking, mouthCue, dt)
    this.lastCollisionCorrections = []
    this.resolveActorCollisions()
    this.storyProps.update(progress, ambient, (character) => this.actors.positionOf(character),
      (character) => this.actors.mouthPositionOf(character), dt)
    this.set.setOwnedProps(this.storyProps.ownedProps)
    this.set.update(this.beat, progress, ambient, (character) => this.actors.positionOf(character))
    // Resolve all authored moving roots after their blocking passes have run.
    // Previously each subsystem checked only its own seabed, so a prop could be
    // valid in isolation while still tunnelling through an actor or another prop.
    this.storyProps.updateAttachments(character => this.actors.matrixOf(character))
    this.resolveDynamicCollisions()
    this.ambientElapsed += Math.max(0, dt)
    this.extras.update(progress, ambient, (character) => this.actors.positionOf(character), this.blockingBodies())

    this.atmosphere.update(this.ambientElapsed)
    const drift = -Math.expm1(-Math.max(0, dt) * 6)
    this.cameraTarget.copy(this.cameraBase)
    this.lookTarget.copy(this.lookBase)
    const subject = this.beat.actions.find(cue => ['walk', 'chase', 'enter'].includes(cue.type))?.actor
      ?? (this.beat.cast.includes('bluebob') ? 'bluebob' : this.beat.cast[0])
    const subjectPosition = subject ? this.actors.positionOf(subject) : new THREE.Vector3()
    if (this.anchorPending) {
      this.cameraAnchor.copy(subjectPosition)
      this.anchorPending = false
    }
    if (this.beat.camera === 'tracking' || this.beat.camera === 'chase') {
      const offset = subjectPosition.clone().sub(this.cameraAnchor)
      offset.y = 0
      this.cameraTarget.add(offset)
      this.lookTarget.add(offset)
    }
    if (this.beat.shot === 'ending-5b') {
      const pullback = THREE.MathUtils.smoothstep(progress, 0, 1)
      this.cameraTarget.addScaledVector(this.cameraBase.clone().sub(this.lookBase), pullback * 0.4)
    }
    this.cameraTarget.x += Math.sin(this.ambientElapsed * 0.22) * 0.16 * (this.reducedMotion ? 0 : 1)
    this.cameraTarget.y += Math.cos(this.ambientElapsed * 0.19) * 0.08 * (this.reducedMotion ? 0 : 1)
    this.camera.position.lerp(this.cameraTarget, drift)
    this.lookCurrent.lerp(this.lookTarget, drift)
    this.camera.lookAt(this.lookCurrent)
    this.camera.updateMatrixWorld(true)
    // Optical-axis distance, rather than Euclidean range, matches the depth texture.
    const focus = this.lookCurrent.clone().applyMatrix4(this.camera.matrixWorldInverse)
    this.targetFocusDistance = Math.max(1, -focus.z)
    this.focusDistance = smoothFocusDistance(this.focusDistance, this.targetFocusDistance, dt)
    this.scene.userData.focusDistance = this.focusDistance
  }

  resize(aspect: number): void {
    if (!Number.isFinite(aspect) || aspect <= 0) return
    this.camera.aspect = aspect
    if (this.beat) this.composeCamera(this.beat)
    else this.camera.updateProjectionMatrix()
  }

  clear(): void {
    this.beat = null
    this.skyboxScene = null
    this.anchorPending = true
    this.focusDistance = 15
    this.targetFocusDistance = 15
    this.scene.userData.focusDistance = this.focusDistance
    this.set?.dispose()
    this.set = null
    this.actors.setCast([])
    this.extras.root.visible = false
    this.storyProps.root.visible = false
  }

  private dynamicBodies(): CollisionBody[] {
    return [
      ...this.blockingBodies(),
      ...this.extras.collisionBodies(),
    ]
  }

  private blockingBodies(): CollisionBody[] {
    return [
      ...this.actors.collisionBodies(),
      ...this.storyProps.collisionBodies(),
      ...(this.set?.collisionBodies() ?? []),
    ]
  }

  private resolveActorCollisions(): void {
    const actors = this.actors.collisionBodies()
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < actors.length; i++) {
        for (let j = i + 1; j < actors.length; j++) {
          const a = actors[i]!, b = actors[j]!
          if (this.allowsActorContact(a.name, b.name)) continue
          if (separateXZ(b, a, 0.08)) {
            this.lastCollisionCorrections.push(`${b.name}>${a.name}@${b.object.position.x.toFixed(3)},${b.object.position.z.toFixed(3)}`)
          }
        }
      }
    }
  }

  private resolveDynamicCollisions(): void {
    if (!this.beat) return
    const actors = this.actors.collisionBodies()
    const props = [...this.storyProps.collisionBodies(), ...(this.set?.collisionBodies() ?? [])]
    const attached = props.filter(prop => prop.object.userData.collisionAttached)
    const placed = [...attached]
    for (const prop of props) {
      if (attached.includes(prop)) continue
      const blockers = [
        ...actors.filter(actor => !this.allowsContact(prop.name, actor.name)),
        ...placed.filter(other => !this.allowsPropContact(prop.name, other.name)),
      ]
      const offset = clearPlacementXZ(boundsOf(prop.object), blockers.map(body => boundsOf(body.object)))
      if (offset.x !== 0 || offset.z !== 0) {
        const world = prop.object.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(offset.x, 0, offset.z))
        prop.object.position.copy(prop.object.parent ? prop.object.parent.worldToLocal(world) : world)
        prop.object.updateWorldMatrix(true, true)
        this.lastCollisionCorrections.push(`${prop.name}>${blockers.map(body => body.name).join(',')}@${prop.object.position.x.toFixed(3)},${prop.object.position.z.toFixed(3)}`)
      }
      placed.push(prop)
    }
  }

  private allowsContact(propName: string, actorName: string): boolean {
    if (!this.beat) return false
    const actor = actorName.replace(/^DioramaActor_/, '')
    if (propName === 'StorySatchel' && actor === 'bluebob' && this.beat.cast.includes('bluebob') && this.beat.shot !== 'intro-1a') return true
    if (this.beat.shot === 't6-tilt-02' && propName === 'StorySatchel' && actor === 'zap'
      && this.beat.actions.some(action => action.actor === 'zap' && action.type === 'catch')) return true
    // In the bridge beat Bob is planted on the surviving bubble while Zap pops
    // the next one. The wide animated holder contains that contact by design;
    // it must not be treated as an accidental body intersection.
    if (this.beat.shot === 't4-2a-02' && /BridgeBubbles_Holder$/.test(propName)
      && (actor === 'bluebob' || actor === 'zap')) return true
    if (actor === 'bluebob' && ['StorySurvivingPlate', 'StoryPlates'].includes(propName)
      && /^intro-(2a-01|2b|2c|3a-01|4b|4e-02)$/.test(this.beat.shot)) return true
    const propMap: Partial<Record<string, string>> = {
      'coin-chest': 'StoryFeastChest',
      'coin-bag': 'StoryLastBag',
      sandwich: 'StorySandwich',
      'giant-sandwich': 'StorySandwichCenter',
      map: 'StoryCargoChart',
      'ultimate-sandwich': 'StorySandwich',
      patty: 'StorySandwichCenter',
      'jelly-jars': 'DioramaProp_jelly-jars',
      decorations: 'DioramaProp_decorations',
      bridge: 'DioramaProp_bridge',
    }
    return this.beat.actions.some((action) => {
      if (!action.prop || action.actor !== actor) return false
      const expected = propMap[action.prop]
      // Blender-authored roots have a stable prefix while the procedural
      // continuity copy carries the Story* name. Treat both as the same
      // screenplay object for collision contacts.
      const propMatches = expected === propName
        || (action.prop === 'bridge' && /BridgeBubbles_Holder$/.test(propName))
        || (action.prop === 'coin-bag' && /CoinBag/.test(propName))
        || (action.prop === 'patty' && /TreasurePatty/.test(propName))
        || (action.prop === 'sandwich' && /DioramaProp_sandwich/.test(propName))
      if (!propMatches) return false
      return ['catch', 'collect', 'eat', 'offer', 'offer-coin', 'pull', 'reward', 'serve', 'spill', 'steal', 'swarm-carry', 'throw', 'hug-prop', 'anchor', 'build', 'decorate', 'pop-bubble'].includes(action.type)
    })
  }

  private allowsPropContact(a: string, b: string): boolean {
    const pair = new Set([a, b])
    return (pair.has('StoryPlates') && pair.has('StorySurvivingPlate'))
      || (pair.has('StorySandwichRing') && pair.has('StorySandwichCenter'))
  }

  private allowsActorContact(a: string, b: string): boolean {
    if (!this.beat) return false
    // The rescue shot is deliberately a hand catch before both performers
    // fall together. Their proxy volumes overlap because that is the contact
    // being staged; every other actor pair must clear in world space.
    return this.beat.shot === 't6-tilt-02'
      || this.beat.shot === 'ending-4c-03'
  }

  private composeCamera(beat: Beat): void {
    const speaker = beat.dialogue?.speaker
    const speakerPosition = speaker ? this.actors.positionOf(speaker) : new THREE.Vector3()
    switch (beat.camera) {
      case 'wide':
        this.cameraBase.set(7.2, 9.4, 19.5)
        this.lookBase.set(0, 1.35, -0.75)
        this.camera.fov = 32
        break
      case 'two-shot':
        this.cameraBase.set(4.2, 6.6, 17.8)
        this.lookBase.set(0, 1.42, 0)
        this.camera.fov = 31
        break
      case 'close':
        this.cameraBase.set(speakerPosition.x * 0.8 + 3.1, 4.6, 12.5)
        this.lookBase.set(speakerPosition.x * 0.8, 1.5, 0.1)
        this.camera.fov = 28
        break
      case 'reveal':
        this.cameraBase.set(10, 9.5, 19.5)
        this.lookBase.set(0, 1.5, -1.1)
        this.camera.fov = 34
        break
      case 'chase':
        this.cameraBase.set(-6.8, 6.5, 17)
        this.lookBase.set(-0.25, 1.4, 0.05)
        this.camera.fov = 34
        break
      case 'low':
        this.cameraBase.set(3, 3.5, 15.5)
        this.lookBase.set(0, 1.7, -0.45)
        this.camera.fov = 31
        break
      case 'insert':
        this.cameraBase.set(2.4, 4.5, 10.5)
        this.lookBase.set(0, 1.05, 0.1)
        this.camera.fov = 30
        break
      case 'reaction':
        this.cameraBase.set(speakerPosition.x * 0.7 - 3.5, 4.4, 12.5)
        this.lookBase.set(speakerPosition.x * 0.7, 1.48, 0.2)
        this.camera.fov = 28
        break
      case 'tracking':
        this.cameraBase.set(-6.4, 7.2, 18)
        this.lookBase.set(0.35, 1.35, -0.1)
        this.camera.fov = 32
        break
      case 'overhead':
        this.cameraBase.set(2, 15, 17)
        this.lookBase.set(0, 0.45, -0.9)
        this.camera.fov = 34
        break
    }
    if (beat.camera !== 'overhead') this.cameraBase.y = this.lookBase.y + (this.cameraBase.y - this.lookBase.y) * 0.43
    this.camera.position.copy(this.cameraBase)
    if (this.camera.aspect < 0.75) {
      this.camera.fov = Math.min(62, this.camera.fov + 14)
      this.cameraBase.z += 5.4
      if (beat.cast.length >= 3) {
        // Portrait group shots must retain both ends of the cast. Following the
        // speaker's x offset otherwise cuts the listener out of the same exchange.
        this.lookBase.x = 0
        this.cameraBase.x *= 0.35
        const halfWidth = beat.cast.length === 4 ? 6.2 : 5.1
        const horizontalSlope = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * this.camera.aspect
        this.cameraBase.z = Math.max(this.cameraBase.z, halfWidth / horizontalSlope + 1.5)
      }
      this.camera.position.copy(this.cameraBase)
    }
    this.camera.updateProjectionMatrix()
    this.camera.lookAt(this.lookBase)
    this.camera.updateMatrixWorld(true)
    this.targetFocusDistance = Math.max(1, -this.lookBase.clone().applyMatrix4(this.camera.matrixWorldInverse).z)
    // A newly composed shot starts at its own optical axis, then subsequent camera drift and shot
    // cuts are eased by update(). This avoids a stale focus plane for the first frame without
    // reintroducing a visible focus-distance step on later frames.
    this.focusDistance = this.targetFocusDistance
    this.scene.userData.focusDistance = this.focusDistance
  }
}

export function isDioramaCharacter(value: string): value is Character {
  return value === 'bluebob' || value === 'krabs' || value === 'king' || value === 'zap'
}
